import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const err = (e) => e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Request failed";
const Box = ({ title, id, children, action }) => (
  <div className="min-w-0 rounded-2xl border border-stone-200 bg-white p-4" data-testid={id}>
    <div className="flex items-center justify-between mb-2">
      <div className="text-xs font-black uppercase tracking-widest text-stone-500">{title}</div>{action}
    </div>
    <div className="overflow-x-auto">{children}</div>
  </div>
);
const Empty = ({ id }) => <div className="text-sm text-stone-400" data-testid={id}>Nothing yet.</div>;
const when = (s) => (s ? new Date(s).toLocaleString() : "—");

export const VoiceCalls = ({ items }) => (
  <Box title="Simulated call history (MOCK)" id="voice-calls">
    {!items.length ? <Empty id="voice-calls-empty" /> : (
      <table className="w-full text-xs"><tbody>
        {items.map((c) => (
          <tr key={c.call_id} className="border-b border-stone-100" data-testid={`voice-call-${c.call_id}`}>
            <td className="py-1 pr-2">{when(c.created_at)}</td><td className="pr-2">{c.caller_masked}</td><td className="pr-2">{c.lang}</td>
            <td className="pr-2">{c.identity}</td><td className="pr-2 font-bold">{c.status}</td>
            <td>{(c.completed || []).map((x) => `${x.flow}${x.published ? "·published" : "·private"}`).join(", ") || c.flow || "—"}</td>
          </tr>
        ))}
      </tbody></table>
    )}
  </Box>
);

export const VoiceCallbacks = ({ items, reload }) => {
  const [busy, setBusy] = useState(false);
  const processNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/voice/callbacks/process"); toast.success(`Processed ${data.processed} (MOCK, no calls placed)`); reload(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const replay = async (id) => {
    const reason = window.prompt("Reason for replaying this failed callback (audit logged)");
    if (!reason || reason.trim().length < 5) return;
    try { await api.post(`/admin/voice/callbacks/${id}/replay`, { reason }); toast.success("Re-queued"); reload(); }
    catch (e) { toast.error(err(e)); }
  };
  return (
    <Box title="Callback queue (simulated)" id="voice-callbacks" action={
      <button onClick={processNow} disabled={busy} data-testid="voice-callbacks-process" className="h-8 px-3 rounded-full bg-stone-900 text-white text-xs font-bold disabled:opacity-40">Process due now</button>}>
      {!items.length ? <Empty id="voice-callbacks-empty" /> : (
        <table className="w-full text-xs"><tbody>
          {items.map((c) => (
            <tr key={c.callback_id} className="border-b border-stone-100" data-testid={`voice-cb-${c.callback_id}`}>
              <td className="py-1 pr-2">{c.mobile_masked}</td><td className="pr-2">{c.reason}</td>
              <td className="pr-2 font-bold" data-testid={`voice-cb-status-${c.callback_id}`}>{c.status}</td>
              <td className="pr-2">{c.status_reason || ""}</td><td className="pr-2">{c.attempts}/{c.max_attempts}</td><td className="pr-2">{when(c.next_attempt_at)}</td>
              <td>{c.status === "failed" && <button onClick={() => replay(c.callback_id)} data-testid={`voice-cb-replay-${c.callback_id}`} className="underline font-bold">Replay</button>}</td>
            </tr>
          ))}
        </tbody></table>
      )}
    </Box>
  );
};

export const VoiceConsents = ({ items, reload }) => {
  const [mobile, setMobile] = useState("");
  const optOut = async () => {
    try { const { data } = await api.post("/admin/voice/consents/opt-out", { mobile, reason: "Caller asked to stop calls" }); toast.success(`Opted out · ${data.cancelled_callbacks} callbacks cancelled`); setMobile(""); reload(); }
    catch (e) { toast.error(err(e)); }
  };
  return (
    <Box title="Consent & opt-outs" id="voice-consents">
      <div className="flex gap-2 mb-2">
        <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile to opt out" className="h-9 flex-1 rounded-xl border border-stone-200 px-3 text-sm" data-testid="voice-optout-mobile" />
        <button onClick={optOut} disabled={mobile.replace(/\D/g, "").length < 10} data-testid="voice-optout-submit" className="h-9 px-3 rounded-full border border-red-300 text-red-700 text-xs font-bold disabled:opacity-40">Record opt-out</button>
      </div>
      {!items.length ? <Empty id="voice-consents-empty" /> : (
        <table className="w-full text-xs"><tbody>
          {items.map((r) => (
            <tr key={r.mobile_masked + r.updated_at} className="border-b border-stone-100" data-testid="voice-consent-row">
              <td className="py-1 pr-2">{r.mobile_masked}</td><td className="pr-2">callbacks {r.callbacks ? "yes" : "no"}</td>
              <td className="pr-2">{r.opted_out ? "OPTED OUT" : ""}</td><td className="pr-2">{r.interest ? `${r.interest.skill || ""} · ${r.interest.city || ""}` : ""}</td>
              <td>{r.history_count} changes · {when(r.updated_at)}</td>
            </tr>
          ))}
        </tbody></table>
      )}
    </Box>
  );
};

export const VoiceSuspicious = ({ items }) => (
  <Box title="Vacancies held for safety review" id="voice-suspicious">
    {!items.length ? <Empty id="voice-suspicious-empty" /> : (
      <table className="w-full text-xs"><tbody>
        {items.map((r) => (
          <tr key={r.listing_id} className="border-b border-stone-100" data-testid={`voice-sus-${r.listing_id}`}>
            <td className="py-1 pr-2">{r.title}</td><td className="pr-2">{r.city}</td><td className="pr-2">₹{r.price}/{r.price_unit}</td>
            <td className="pr-2">{r.extra?.voice_flag_reason}</td><td>{r.case ? <a href="/admin/moderation" className="underline">{r.case.case_id} · {r.case.status}</a> : "—"}</td>
          </tr>
        ))}
      </tbody></table>
    )}
  </Box>
);
